if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');

        this.selectors = {
          variantId: '[name="id"]',
        };

        this.isSyncingFromMain = false;
        this.restoreScrollAfterSync = false;
        this.savedScrollY = 0;
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
      get productId() {
        return this.getAttribute('data-product-id');
      }
      get variantSelect() {
        return this.querySelector(this.selectors.variantId);
      }

      // product-info owns the "shopper hasn't picked a size yet" state; the bar
      // reads it off the attribute so it doesn't matter which element upgrades
      // first.
      get optionsPending() {
        return this.mainProductInfo?.dataset.optionsPending === 'true';
      }

      connectedCallback() {
        // Hyper's main product form carries no data-product-id (unlike Skechers),
        // so it is looked up by class only; product-info does carry the attribute.
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector(`product-info[data-product-id="${this.productId}"]`);
        this.container = this.closest('.sticky-atc-bar');

        this.variantData = this.getVariantData();
        this.colorIndex = parseInt(this.dataset.colorIndex, 10);
        this.hiddenVariantIds = this.getHiddenVariantIds();
        this.colorImageMap = this.buildColorImageMap();

        this.currentVariant = this.resolveVariant();

        if (this.optionsPending) this.showSizePlaceholder();

        // [name="id"] es siempre un input hidden: el dropdown propio escribe ahí
        // y despacha `change`, así que un solo listener cubre todo.
        this.onOptionChange = this.onOptionChange.bind(this);
        if (this.variantSelect) {
          this.variantSelect.addEventListener('change', this.onOptionChange);
        }

        this.initSizePicker();

        this.submitButton = this.querySelector('[name="add"]');
        if (this.submitButton) {
          this.onAddClickGuard = this.onAddClickGuard.bind(this);
          this.submitButton.addEventListener('click', this.onAddClickGuard, true);
        }

        // Propagating a change to the main PDP picker makes product-info re-render
        // and .focus() the (off-screen) main option input (product-info.js:258),
        // which scrolls the page. Undo that scroll when focus lands inside the main
        // product info after a sticky-initiated change. Runs before paint, so no
        // visible jump.
        this.onDocumentFocusIn = this.onDocumentFocusIn.bind(this);
        document.addEventListener('focusin', this.onDocumentFocusIn, true);

        this.init();
        this.syncWithMainProductForm();
      }

      disconnectedCallback() {
        this.unsubscribeVariant?.();
        this.observer?.disconnect();
        document.removeEventListener('focusin', this.onDocumentFocusIn, true);
        this.submitButton?.removeEventListener('click', this.onAddClickGuard, true);
        this.sizePicker?.removeEventListener('click', this.onPickerClick);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
        if (this.mql) this.mql.onchange = null;
        clearTimeout(this.restoreScrollTimer);
      }

      onDocumentFocusIn(event) {
        if (!this.restoreScrollAfterSync) return;
        if (this.mainProductInfo && this.mainProductInfo.contains(event.target)) {
          window.scrollTo({ top: this.savedScrollY, left: 0, behavior: 'instant' });
          this.restoreScrollAfterSync = false;
        }
      }

      getVariantData() {
        this.variantData =
          this.variantData || JSON.parse(this.container.querySelector('[data-sticky-variant-json]').textContent);
        return this.variantData;
      }

      getHiddenVariantIds() {
        try {
          return new Set(JSON.parse(this.dataset.hiddenVariantIds || '[]'));
        } catch (e) {
          return new Set();
        }
      }

      hasColor() {
        return !isNaN(this.colorIndex) && this.colorIndex >= 0;
      }

      // A variant counts as available in the bar only if it is in stock AND not
      // hidden via the hide_variant metafield (PDP parity).
      isAvailable(variant) {
        return !!variant && variant.available && !this.hiddenVariantIds.has(variant.id);
      }

      init() {
        if (!this.productFormActions) {
          this.container.classList.add('sticky-atc-bar--show');
          return;
        }

        this.mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        this.mql.onchange = this.checkDevice.bind(this);
        this.checkDevice();

        const rootMargin = `${this.productFormActions.offsetHeight}px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.container.classList.toggle('sticky-atc-bar--show', !entry.isIntersecting);
            });
          },
          { threshold: 1, rootMargin }
        );
        this.observer.observe(this.productFormActions);
      }

      checkDevice() {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
      }

      // -- selection & option building --

      resolveVariant() {
        const el = this.variantSelect;
        if (!el) return null;
        return this.variantData.find((variant) => variant.id === Number(el.value)) || null;
      }

      // The size (non-color option values, " / "-joined). The color is left out
      // because the list is already scoped to the selected color. Falls back to
      // the variant title when the product has no color option. Must match the
      // label the Liquid renders so a rebuild is seamless.
      buildLabel(variant) {
        if (!this.hasColor()) return variant.title;
        const parts = [];
        variant.options.forEach((value, i) => {
          if (i !== this.colorIndex) parts.push(value);
        });
        return parts.length ? parts.join(' / ') : variant.options[this.colorIndex];
      }

      // Rebuild the panel to list only the variants of the given variant's color
      // (hidden variants omitted, sold-out ones disabled), then select it.
      rebuildOptions(variant) {
        const panel = this.sizePanel;
        if (!panel || !variant) return;

        const color = this.hasColor() ? variant.options[this.colorIndex] : null;
        const heading = panel.querySelector('.sticky-size-picker__heading');
        const outOfStock = this.sizePicker?.dataset.outOfStock || '';
        const frag = document.createDocumentFragment();

        this.variantData.forEach((v) => {
          if (this.hiddenVariantIds.has(v.id)) return;
          if (color !== null && v.options[this.colorIndex] !== color) return;

          const option = document.createElement('button');
          option.type = 'button';
          option.setAttribute('role', 'option');
          option.className = 'sticky-size-picker__option';
          option.dataset.variantId = v.id;
          option.disabled = !v.available;
          option.setAttribute('aria-selected', v.id === variant.id ? 'true' : 'false');
          option.innerHTML =
            `<span class="sticky-size-picker__option-label">${this.buildLabel(v)}</span>` +
            (v.available ? '' : `<span class="sticky-size-picker__option-note">${outOfStock}</span>`);
          frag.appendChild(option);
        });

        panel.replaceChildren(frag);
        if (heading) panel.prepend(heading);

        if (this.variantSelect) this.variantSelect.value = String(variant.id);
        this.syncPickerLabel();

        // El usuario cambió de color sin haber elegido talle.
        if (this.optionsPending) this.showSizePlaceholder();
      }

      // Hasta que se elige talle el trigger dice "Elegí tu talle" y el form no
      // lleva variant id. El botón queda habilitado a propósito: el gris se leía
      // como "sin stock"; lo que frena el submit es onAddClickGuard.
      showSizePlaceholder() {
        if (this.variantSelect) this.variantSelect.value = '';
        this.syncPickerLabel();
      }

      // -- dropdown propio de talle --

      get sizePicker() {
        return this.querySelector('[data-sticky-size-picker]');
      }
      get sizePanel() {
        return this.querySelector('[data-sticky-size-panel]');
      }
      get sizeTrigger() {
        return this.querySelector('[data-sticky-size-trigger]');
      }

      initSizePicker() {
        if (!this.sizePicker) return;

        this.onPickerClick = this.onPickerClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);

        this.sizePicker.addEventListener('click', this.onPickerClick);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);

        this.syncPickerLabel();
      }

      onPickerClick(event) {
        const trigger = event.target.closest('[data-sticky-size-trigger]');
        if (trigger) {
          this.toggleSizePanel(trigger.getAttribute('aria-expanded') !== 'true');
          return;
        }

        const option = event.target.closest('.sticky-size-picker__option');
        if (!option || option.disabled) return;

        const input = this.variantSelect;
        if (input) {
          input.value = option.dataset.variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.toggleSizePanel(false);
      }

      toggleSizePanel(open) {
        const panel = this.sizePanel;
        const trigger = this.sizeTrigger;
        if (!panel || !trigger) return;
        panel.hidden = !open;
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      onDocumentClick(event) {
        if (this.sizePicker && this.sizePicker.contains(event.target)) return;
        this.toggleSizePanel(false);
      }

      onDocumentKeydown(event) {
        if (event.key !== 'Escape') return;
        this.toggleSizePanel(false);
      }

      // Espeja el hidden input en el label del trigger y en los aria-selected.
      syncPickerLabel() {
        const picker = this.sizePicker;
        const trigger = this.sizeTrigger;
        if (!picker || !trigger) return;

        const label = trigger.querySelector('[data-sticky-size-label]');
        const variant = this.resolveVariant();

        if (label) {
          const template = picker.dataset.valueTemplate || '[value]';
          label.textContent = variant
            ? template.replace('[value]', this.buildLabel(variant))
            : picker.dataset.placeholder || FoxTheme.variantStrings.chooseSize;
        }
        trigger.classList.toggle('is-filled', !!variant);
        if (variant) trigger.classList.remove('has-error');

        this.querySelectorAll('.sticky-size-picker__option').forEach((option) => {
          const on = !!variant && Number(option.dataset.variantId) === variant.id;
          option.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }

      // Igual que en la PDP: se cancela el click del botón, no el submit del
      // form. stopImmediatePropagation y no stopPropagation, porque puede haber
      // otro listener sobre el mismo botón y stopPropagation solo frena ancestros.
      onAddClickGuard(event) {
        if (!this.optionsPending) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        this.toggleSizePanel(true);
        this.sizeTrigger?.classList.add('has-error');

        document.dispatchEvent(
          new CustomEvent('size:required', { detail: { productId: this.productId } })
        );
      }

      // ¿Queda algún talle por elegir? Si no hay panel (producto de una sola
      // variante) cae a la disponibilidad de la variante resuelta.
      hasPickableSize() {
        const options = this.querySelectorAll('.sticky-size-picker__option');
        if (!options.length) return this.isAvailable(this.resolveVariant());
        return Array.from(options).some((option) => !option.disabled);
      }

      buildColorImageMap() {
        const map = {};
        if (!this.hasColor()) return map;
        this.variantData.forEach((variant) => {
          const color = variant.options[this.colorIndex];
          if (map[color]) return;
          if (variant.featured_image && variant.featured_image.src) {
            map[color] = variant.featured_image.src;
          }
        });
        return map;
      }

      // -- change handling (sticky origin) --

      onOptionChange() {
        if (this.isSyncingFromMain) return;
        if (this.variantSelect && this.variantSelect.value === '') return;

        const variant = this.resolveVariant();
        this.currentVariant = variant;

        if (!variant) {
          this.applyVariantToBar(variant);
          return;
        }

        // Remember scroll position so the main picker's post-render focus
        // (product-info.js) does not scroll the page away from the bar. The guard
        // is generous so a slow section re-render still gets its scroll restored.
        this.savedScrollY = window.scrollY;
        this.restoreScrollAfterSync = true;
        clearTimeout(this.restoreScrollTimer);
        this.restoreScrollTimer = setTimeout(() => {
          this.restoreScrollAfterSync = false;
        }, 4000);

        // Propagate before applying: picking a size here clears product-info's
        // pending state synchronously, so applyVariantToBar won't snap the select
        // back to the placeholder for the length of the re-render.
        this.propagateToMain();
        this.applyVariantToBar(variant);
      }

      applyVariantToBar(variant) {
        if (!variant) {
          // Sin variante y con talle pendiente es el estado normal de entrada, no
          // una combinación no disponible: no se toca el botón.
          if (!this.optionsPending) {
            this.updateButton(true, FoxTheme.variantStrings.unavailable, true);
          } else {
            this.showSizePlaceholder();
          }
          return;
        }
        if (this.variantSelect) this.variantSelect.value = variant.id;
        this.syncPickerLabel();
        this.updatePrice();
        this.updateImage();
        // Mientras falta el talle, `variant` es la que resolvió el color y puede
        // ser un talle agotado dentro de un color con stock: deshabilitar por eso
        // se lee como "sin stock". Se mira si queda alguna fila elegible. Las
        // variantes ocultas (hide_variant) cuentan como agotadas.
        const soldOut = this.optionsPending ? !this.hasPickableSize() : !this.isAvailable(variant);
        this.updateButton(soldOut, FoxTheme.variantStrings.soldOut, false);

        // The price and image above track the colour's first variant, but the
        // shopper still owes us a size.
        if (this.optionsPending) this.showSizePlaceholder();
      }

      // -- sync sticky -> main --

      propagateToMain() {
        if (!this.currentVariant) return;
        const mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
        if (!mainVariantSelects) return;

        // Re-query on every change: the PDP re-render replaces these nodes.
        const groups = Array.from(mainVariantSelects.children).filter((el) =>
          el.classList.contains('product-form__input')
        );

        // Dispatch only for options whose value actually changed. The color radio
        // is already checked (color changes only in the PDP), so only size fires.
        this.currentVariant.options.forEach((value, i) => {
          const group = groups[i];
          if (!group) return;

          const radio = group.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
          if (radio) {
            if (!radio.checked) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
            return;
          }

          const mainSelect = group.querySelector('select');
          if (mainSelect && mainSelect.value !== value) {
            // variant-selects reads 'select option[selected]' (the attribute), so
            // move the attribute too — setting .value alone is not enough.
            const options = Array.from(mainSelect.options);
            options.forEach((option) => option.removeAttribute('selected'));
            const selectedOption = options.find((option) => option.value === value);
            if (selectedOption) selectedOption.setAttribute('selected', 'selected');
            mainSelect.value = value;
            mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      // -- sync main -> sticky --

      syncWithMainProductForm() {
        this.unsubscribeVariant = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const mainSectionId =
            this.mainProductInfo?.dataset.originalSection || this.mainProductInfo?.dataset.section;
          if (!mainSectionId || event.data.sectionId !== mainSectionId) return;

          const variant = event.data.variant;
          this.isSyncingFromMain = true;
          try {
            this.currentVariant = variant;
            // Rebuild the dropdown for the incoming variant's color (covers a color
            // change in the PDP) and select it.
            if (variant) this.rebuildOptions(variant);
            this.applyVariantToBar(variant);
            this.updateQuantityFromHtml(event.data.html);
          } finally {
            this.isSyncingFromMain = false;
          }
        });
      }

      // -- quantity --

      // The variantChange payload carries the full main-product section render,
      // which includes this sticky block — swap the quantity wrapper from it so
      // per-variant quantity rules stay correct without an extra fetch.
      updateQuantityFromHtml(html) {
        if (!html) return;
        const quantity = document.querySelector(`#QuantitySticky-${this.sectionId}`);
        const newQuantity = html.querySelector(`#QuantitySticky-${this.sectionId}`);
        if (quantity && newQuantity) {
          quantity.innerHTML = newQuantity.innerHTML;
        }
      }

      // -- UI updates --

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('.sticky-atc-bar__form');
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text && addButtonText) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          if (addButtonText) addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }
      }

      updateImage() {
        const img = this.querySelector('.sticky-atc-bar__product-image img');
        if (!img || !this.currentVariant) return;

        const src =
          this.currentVariant.featured_image && this.currentVariant.featured_image.src
            ? this.currentVariant.featured_image.src
            : this.hasColor()
            ? this.colorImageMap[this.currentVariant.options[this.colorIndex]]
            : null;
        if (!src) return;

        const sized = (w) => src.replace(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i, `_${w}x.$1$2`);
        img.srcset = `${sized(90)} 90w, ${sized(180)} 180w`;
        img.src = sized(180);
      }

      updatePrice() {
        if (!this.currentVariant) return;
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        if (!priceWrapper) return;
        const unitPrice = unitPriceWrapper && unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out (or hidden via hide_variant).
        if (!this.isAvailable(this.currentVariant)) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        // Hyper's price.liquid wraps compare-at in <s> — keep the strikethrough
        // (the Skechers original writes plain text here).
        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const baseUnit =
            FoxTheme.Currency && typeof FoxTheme.Currency.getBaseUnit === 'function'
              ? FoxTheme.Currency.getBaseUnit(this.currentVariant)
              : '';
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            this.currentVariant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${baseUnit}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else if (unitPriceWrapper) {
          unitPriceWrapper.classList.add('hidden');
        }
      }
    }
  );
}
